// components/CrewList.js
import React, { memo } from 'react';
import {
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Chip
} from '@material-ui/core';
import { Search, Clear } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { calculateCrewDayHours, calculateCrewWeeklyHours, getCrewWorkloadStatus } from '../utils/crewUtils';
import { debugHoursCalculation, debugWeeklyHours, compareCalculationMethods } from '../utils/debugHours';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    width: '100%',
    paddingBottom: theme.spacing(10),
    marginBottom: theme.spacing(10),
  },
  searchArea: {
    padding: theme.spacing(2),
  },
  searchLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  crewList: {
    backgroundColor: theme.palette.grey[100],
    margin: theme.spacing(0.5),
    padding: theme.spacing(1),
    height: '60vh',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
    paddingBottom: 100
  },
  crewCard: {
    cursor: 'pointer',
    backgroundColor: '#fff',
    padding: theme.spacing(1.5),
    margin: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)',
    },
  },
  selectedCrewCard: {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
  crewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  crewName: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  crewStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  statItem: {
    textAlign: 'center',
    flex: 1,
  },
  statValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  weekDisplay: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  dayItem: {
    textAlign: 'center',
    flex: 1,
    cursor: 'pointer',
    padding: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedDay: {
    backgroundColor: theme.palette.action.selected,
  },
  dayLabel: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
  },
  dayHours: {
    fontSize: '0.875rem',
  },
  workloadChip: {
    height: 20,
    fontSize: '0.7rem',
    textTransform: 'capitalize',
  },
  underutilized: { backgroundColor: theme.palette.info.main, color: theme.palette.common.white },
  optimal: { backgroundColor: theme.palette.success.main, color: theme.palette.common.white },
  busy: { backgroundColor: theme.palette.warning.main, color: theme.palette.common.white },
  overtime: { backgroundColor: theme.palette.error.main, color: theme.palette.common.white },
}));

const colorMap = {
  success: 'primary',
  error: 'secondary',
  warning: 'default',
  info: 'default',
};

const labelMap = {
  underutilized: 'Underutilized',
  optimal: 'Optimal',
  busy: 'Busy',
  overtime: 'Overtime',
};

const CrewCard = memo(({ 
  crew, 
  isSelected,
  weeklyHours, 
  weeklyRoutes,
  dayOfWeek, 
  onCrewSelect, 
  onDayClick 
}) => {
  const classes = useStyles();
  
  // Debug logging for selected crew
  if (isSelected) {
    console.log(`🔍 DEBUG: ${crew.name} Hours Calculation`);
    debugWeeklyHours(weeklyHours, crew._id);
    if (weeklyRoutes) {
      compareCalculationMethods(weeklyRoutes, weeklyHours, crew._id, dayOfWeek);
    }
  }
  
  const todayHours = calculateCrewDayHours(weeklyHours, crew._id, dayOfWeek);
  const weekHours = calculateCrewWeeklyHours(weeklyHours, crew._id);
  const workloadStatus = getCrewWorkloadStatus(todayHours.total);
  // console.log('Todays horus...', todayHours)
  // console.log('workloadStatus...', workloadStatus)
  const days = ['M', 'T', 'W', 'Th', 'F'];
  const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  // const status = getCrewWorkloadStatus(todayHours.total);
  const chipColor = colorMap[workloadStatus.color]; // maps to primary/secondary/default
  const chipLabel = labelMap[workloadStatus.status]; // nicer label


  return (
    <div
      className={`${classes.crewCard} ${isSelected ? classes.selectedCrewCard : ''}`}
      style={{ borderRight: `12px solid ${crew.color}` }}
      onClick={() => onCrewSelect(crew)}
    >
      <div className={classes.crewHeader}>
        <Typography className={classes.crewName}>
          {crew.name}
        </Typography>

        <Chip
          label={chipLabel}
          className={clsx(classes.workloadChip, classes[workloadStatus.status])}
          size="small"
        />
      </div>
      
      <div className={classes.crewStats}>
        <div className={classes.statItem}>
          <div className={classes.statValue}>{todayHours.total.toFixed(1)}</div>
          <div className={classes.statLabel}>Hours Today</div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statValue}>{weekHours.total.toFixed(1)}</div>
          <div className={classes.statLabel}>Hours This Week</div>
        </div>
      </div>
      
      <div className={classes.weekDisplay}>
        {days.map((day, index) => {
          const dayData = weeklyHours[crew._id]?.[fullDays[index]] || { serviceTime: 0, driveTime: 0 };
          const dayTotal = dayData.serviceTime + dayData.driveTime;
          const isSelectedDay = fullDays[index] === dayOfWeek && isSelected;
          // console.log('dayData..', dayData, dayTotal)
          return (
            <div
              key={day}
              className={`${classes.dayItem} ${isSelectedDay ? classes.selectedDay : ''}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onDayClick(crew, fullDays[index], e)
              }}
            >
              <div className={classes.dayLabel}>{day}</div>
              <div className={classes.dayHours}>
                {Math.round(dayTotal * 10) / 10}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const CrewList = ({
  crews,
  selectedCrew,
  weeklyHours,
  weeklyRoutes,
  dayOfWeek,
  searchTerm,
  onSearchChange,
  onClearSearch,
  onCrewSelect,
  onDayClick
}) => {
  const classes = useStyles();
  // console.log('Crew  List...', crews, selectedCrew, weeklyHours, dayOfWeek)  
  return (
    <Paper className={classes.root}>
      <div className={classes.searchArea}>
        <Typography className={classes.searchLabel}>
          Search Crews
        </Typography>
        <TextField
          placeholder="Search by name or foreman"
          fullWidth
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={onClearSearch}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      </div>
      
      <div className={classes.crewList}>
        {crews.map((crew) => (
          <CrewCard
            key={crew._id}
            crew={crew}
            isSelected={selectedCrew?._id === crew._id}
            weeklyHours={weeklyHours}
            weeklyRoutes={weeklyRoutes}
            dayOfWeek={dayOfWeek}
            onCrewSelect={onCrewSelect}
            onDayClick={onDayClick}
          />
        ))}
      </div>
    </Paper>
  );
};

export default memo(CrewList);
